import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../api';
import { Button, Loading } from '../ui';

interface VendorBackfillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export function VendorBackfillModal({
  isOpen,
  onClose,
  onSuccess,
  onNotify,
}: VendorBackfillModalProps) {
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPreviewData(null);
      setError(null);
      return;
    }

    let isCancelled = false;
    setLoading(true);
    setError(null);

    api.vendors
      .backfillPreview()
      .then((data) => {
        if (!isCancelled) {
          setPreviewData(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setError(getApiErrorMessage(err, '無法載入舊資料回填預覽'));
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleRun = async () => {
    try {
      setRunning(true);
      setError(null);
      const res = await api.vendors.backfillRun();
      onNotify?.(
        `安全回填已完成！已成功更新 ${res.updated_count} 家廠商主檔資料（跳過/無變更：${res.skipped_count} 家）。`,
        'success',
      );
      onSuccess();
      onClose();
    } catch (err) {
      const msg = getApiErrorMessage(err, '執行回填作業失敗，請稍後再試。');
      setError(msg);
      onNotify?.(msg, 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="relative w-full max-w-2xl my-8 overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-teal-700 to-sky-700 px-6 py-4 text-white">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">🔄</span>
            <div>
              <h3 className="font-bold text-lg leading-tight">舊廠商資料安全回填檢測</h3>
              <p className="text-xs text-teal-100">
                以「新廠商合作申請」為統一標準，安全補齊歷史主檔缺漏欄位
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition"
          >
            <span className="text-lg">✕</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-3 text-xs leading-5 text-sky-900">
            <strong>等冪性與安全保證：</strong>
            回填作業依「以原始核准申請為單一資料來源」原則，僅針對歷史遺留的未填或未關聯欄位進行補齊；
            <strong>絕不覆蓋採購已人工編輯的新資料</strong>，可重複執行保證安全。
          </div>

          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
              <Loading />
              <p className="text-xs text-slate-500">正在掃描所有廠商主檔與原始申請資料…</p>
            </div>
          ) : previewData ? (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                  <div className="text-2xl font-bold text-ink">
                    {previewData.scanned_vendors ?? previewData.scanned_count ?? 0}
                  </div>
                  <div className="text-xs text-slate-500">已掃描廠商數</div>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center">
                  <div className="text-2xl font-bold text-amber-800">
                    {previewData.eligible_vendors ?? previewData.planned_changes_count ?? 0}
                  </div>
                  <div className="text-xs text-amber-700">待補齊廠商數</div>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-800">
                    {previewData.responsible_persons_filled ?? 0}
                  </div>
                  <div className="text-xs text-emerald-700">待補負責人人數</div>
                </div>
              </div>

              {/* Candidates List */}
              {(!previewData.candidates || previewData.candidates.length === 0) ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-5 text-center text-sm text-emerald-900">
                  <div className="text-2xl mb-1">✓</div>
                  <strong>目前所有廠商主檔資料皆已最新一致！</strong>
                  <p className="mt-1 text-xs text-emerald-700">
                    負責人、評估問卷關聯、評估證照文件與主檔資格皆已完全同步，無須執行回填。
                  </p>
                </div>
              ) : (
                <div>
                  <h4 className="font-bold text-sm text-ink mb-2">
                    預計執行的補齊項目清單（共 {previewData.candidates.length} 家）：
                  </h4>
                  <div className="max-h-64 overflow-y-auto space-y-2 rounded-xl border border-slate-200 p-2 bg-slate-50/50">
                    {previewData.candidates.map((c: any) => (
                      <div
                        key={c.vendor_id}
                        className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-1 shadow-sm"
                      >
                        <div className="flex items-center justify-between font-bold text-ink">
                          <span className="text-sm">{c.vendor_name}</span>
                          <span className="font-mono text-slate-500">{c.vendor_code}</span>
                        </div>
                        <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
                          {c.actions?.map((act: string, idx: number) => (
                            <li key={idx} className="leading-5">
                              {act}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
          <div className="text-xs text-slate-500">
            {previewData?.candidates?.length ? '點擊右側按鈕開始執行安全回填' : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={running}>
              關閉
            </Button>
            {previewData && (previewData.eligible_vendors > 0 || (previewData.candidates && previewData.candidates.length > 0)) && (
              <Button
                type="button"
                disabled={running}
                onClick={handleRun}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold"
              >
                {running ? '正在執行回填…' : '執行安全回填'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
